import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEnvelope,
    faPhoneAlt,
    faComments,
    faClock,
    faCopy,
    faQuestionCircle,
    faChevronDown,
    faChevronUp,
    faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import BottomMenu from "../components/BottomMenu";
import { useAuth } from "../context/AuthContext";

const API_BASE = "https://boxmealadminapinew.azurewebsites.net";

const ContactPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = useAuth() || {};

    const [selectedFridge, setSelectedFridge] = useState(() => {
        return localStorage.getItem("selectedFridgeId") || null;
    });

    const [copiedText, setCopiedText] = useState("");
    const [expandedFaq, setExpandedFaq] = useState(null);

    const [contactEmail, setContactEmail] = useState(null);
    const [contactPhone, setContactPhone] = useState(null);

    useEffect(() => {
        let abort = false;
        (async () => {
            try {
                if (!token) return;
                const resp = await fetch(
                    `${API_BASE}/api/customers/get-customer-by-token`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const data = await resp.json();
                if (abort) return;
                setContactEmail(data?.contactPersonEmail || null);
                setContactPhone(data?.contactPersonPhone || null);
            } catch (e) {
                console.warn("[ContactPage] Failed to fetch contact info:", e);
            }
        })();
        return () => {
            abort = true;
        };
    }, [token]);

    const phoneToUse = (contactPhone || "0760374616").replace(/\s+/g, "");
    const emailToUse = contactEmail || "support@ziplink.se";

    const formatPhoneSE = (p) => {
        if (!p) return "";
        const m = p.replace(/^\+46/, "0").replace(/[^\d]/g, "");
        if (m.length === 10 && m.startsWith("0")) {
            return `${m.slice(0, 3)}-${m.slice(3, 6)} ${m.slice(6, 8)} ${m.slice(8)}`;
        }
        return p;
    };

    const handleCallClick = () => {
        window.location.href = `tel:${phoneToUse}`;
    };

    const handleEmailClick = () => {
        window.location.href = `mailto:${emailToUse}`;
    };

    const handleCopyToClipboard = (text, type) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedText(type);
            setTimeout(() => setCopiedText(""), 2000);
        });
    };

    const faqData = [
        {
            question: "Hur handlar jag i butiken?",
            answer:
                "Gå till butiken längst till vänster i menyn. Välj den/de produkter du önskar köpa. Klicka på kategorierna för att exponera alla produkter. Klicka på kassa-ikonen i menyraden. Betala med Swish. Håll upp den genererade QR-koden cirka 10-15 cm framför läset. Tadaa! Ha en fortsatt fin dag!"
        },
        {
            question: "Hur ofta fyller ni på med varor?",
            answer:
                "Så ofta det behövs! Vi försöker att se till så att ni aldrig behöver få slut på produkter i er enhet."
        },
        {
            question: "Kan vi önska varor till butiken?",
            answer:
                "Självklart! Vi vill att ska vara nöjda med utbudet i er enhet och försöker i största möjliga mån att anpassa sortimentet därefter. Varmt välkommen att tipsa oss om varor ni tror att flera på arbetsplatsen skulle uppskatta."
        },
        {
            question: "När levererar ni matlådor?",
            answer:
                "Om ditt företag har mat på jobbet sker utkörningar av matlådor på måndagar."
        },
        {
            question: "Hur fungerar låset?",
            answer:
                "När du genomfört ditt köp visas en QR-kod på din skärm. Denna kod visar du upp ungefär 10 cm från låsets kamera. När låset klickar till och blinkar grönt har du 5 sekunder på dig att öppna dörren."
        },
        {
            question: "Varan jag köpt finns inte i butiken",
            answer:
                "Om varan finns i den digitala butiken men inte fysiskt kan detta bero på att en tidigare köpare tagit en annan vara än vad denne betalat för. Har du gjort en betalning men inte fått din vara? Inga problem, kontakta oss så hjälper vi dig!"
        }
    ];

    const toggleFaq = (index) => {
        setExpandedFaq(expandedFaq === index ? null : index);
    };

    return (
        <Container>
            <ContentWrapper>
                <InfoSection>
                    <SectionTitle>
                        <FontAwesomeIcon icon={faClock} />
                        {" "}
                        Support
                    </SectionTitle>
                    <InfoCard>
                        <TimeSlot>
                            <TimeDay>
                                Måndag - Fredag
                            </TimeDay>
                            <TimeHours>
                                08:00 - 17:00
                            </TimeHours>
                        </TimeSlot>
                        <ResponseTime>
                            ⚡ Vi återkommer alltid inom 24 timmar
                        </ResponseTime>
                    </InfoCard>
                </InfoSection>

                <QuickContactSection>
                    <SectionTitle>
                        Snabbkontakt
                    </SectionTitle>
                    <ContactGrid>
                        <ContactCard onClick={() => navigate("/support")} $primary>
                            <IconCircle $primary>
                                <FontAwesomeIcon icon={faComments} />
                            </IconCircle>
                            <CardContent $primary>
                                <CardTitle $primary>
                                    Rapportera fel
                                </CardTitle>
                                <CardDescription $primary>
                                    Snabbaste sättet att få hjälp
                                </CardDescription>
                                <QuickTag>
                                    Rekommenderat
                                </QuickTag>
                            </CardContent>
                            <ArrowIcon>
                                →
                            </ArrowIcon>
                        </ContactCard>

                        <ContactCard onClick={handleCallClick}>
                            <IconCircle>
                                <FontAwesomeIcon icon={faPhoneAlt} />
                            </IconCircle>
                            <CardContent>
                                <CardTitle>
                                    Ring oss
                                </CardTitle>
                                <CardDescription>
                                    {formatPhoneSE(phoneToUse)}
                                </CardDescription>
                                <CopyButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyToClipboard(phoneToUse, "phone");
                                    }}
                                    $copied={copiedText === "phone"}
                                >
                                    <FontAwesomeIcon icon={copiedText === "phone" ? faCheckCircle : faCopy} />
                                    {copiedText === "phone" ? "Kopierat!" : "Kopiera"}
                                </CopyButton>
                            </CardContent>
                        </ContactCard>

                        <ContactCard onClick={handleEmailClick}>
                            <IconCircle>
                                <FontAwesomeIcon icon={faEnvelope} />
                            </IconCircle>
                            <CardContent>
                                <CardTitle>
                                    Email
                                </CardTitle>
                                <CardDescription>
                                    {emailToUse}
                                </CardDescription>
                                <CopyButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyToClipboard(emailToUse, "email");
                                    }}
                                    $copied={copiedText === "email"}
                                >
                                    <FontAwesomeIcon icon={copiedText === "email" ? faCheckCircle : faCopy} />
                                    {copiedText === "email" ? "Kopierat!" : "Kopiera"}
                                </CopyButton>
                            </CardContent>
                        </ContactCard>
                    </ContactGrid>
                </QuickContactSection>

                <FaqSection>
                    <FaqSectionTitle>
                        <FontAwesomeIcon icon={faQuestionCircle} />
                        {" "}
                        Vanliga frågor
                    </FaqSectionTitle>
                    {faqData.map((faq, index) => (
                        <FaqItem key={index}>
                            <FaqQuestion
                                onClick={() => toggleFaq(index)}
                                $expanded={expandedFaq === index}
                            >
                <span>
                  {faq.question}
                </span>
                                <FontAwesomeIcon icon={expandedFaq === index ? faChevronUp : faChevronDown} />
                            </FaqQuestion>
                            <FaqAnswer $expanded={expandedFaq === index}>
                                <FaqAnswerContent>
                                    {faq.answer}
                                </FaqAnswerContent>
                            </FaqAnswer>
                        </FaqItem>
                    ))}
                </FaqSection>
            </ContentWrapper>

            <BottomMenuSpacer />
            <BottomMenu selectedFridgeId={selectedFridge} />
        </Container>
    );
};

export default ContactPage;

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const pulse = keyframes`
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
    }
`;

const Container = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #f6f7fa 0%, #e9ecef 100%);
    padding: 20px 16px;
`;

const ContentWrapper = styled.div`
    max-width: 600px;
    margin: 0 auto;
    animation: ${fadeInUp} 0.6s ease-out;
`;

const QuickContactSection = styled.section`
    margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 20px 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const FaqSectionTitle = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 20px 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ContactGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ContactCard = styled.div`
    background: ${({ $primary }) =>
            $primary
                    ? "linear-gradient(135deg, rgba(37, 37, 37, 0.95) 0%, rgba(55, 65, 81, 0.95) 100%)"
                    : "rgba(255, 255, 255, 0.9)"};
    backdrop-filter: blur(12px);
    border: 1px solid
    ${({ $primary }) =>
            $primary ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.8)"};
    border-radius: 16px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    position: relative;
    overflow: hidden;

    &:before {
        content: "";
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
        transition: left 0.5s ease;
    }

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }

    &:hover:before {
        left: 100%;
    }

    &:active {
        transform: translateY(-2px);
    }
`;

const IconCircle = styled.div`
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: ${({ $primary }) =>
            $primary
                    ? "rgba(255, 255, 255, 0.15)"
                    : "linear-gradient(135deg, #374151 0%, #1f2937 100%)"};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    flex-shrink: 0;
    transition: all 0.3s ease;

    ${ContactCard}:hover & {
        animation: ${pulse} 1s ease-in-out;
    }
`;

const CardContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const CardTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 4px 0;
    color: ${({ $primary }) => ($primary ? "white" : "#1f2937")};
`;

const CardDescription = styled.p`
    font-size: 14px;
    margin: 0 0 8px 0;
    color: ${({ $primary }) => ($primary ? "rgba(255, 255, 255, 0.9)" : "#6b7280")};
`;

const QuickTag = styled.span`
    display: inline-block;
    background: rgba(37, 37, 37, 0.7);
    color: #fff;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const CopyButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    background: ${({ $copied }) => ($copied ? "#10b981" : "rgba(107,114,128,0.1)")};
    color: ${({ $copied }) => ($copied ? "white" : "#6b7280")};
    border: none;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ $copied }) => ($copied ? "#059669" : "rgba(107,114,128,0.2)")};
    }
`;

const ArrowIcon = styled.div`
    font-size: 20px;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.3s ease;

    ${ContactCard}:hover & {
        transform: translateX(4px);
        color: white;
    }
`;

const InfoSection = styled.section`
    margin-bottom: 32px;
`;

const InfoCard = styled.div`
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const TimeSlot = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid rgba(107, 114, 128, 0.1);

    &:last-of-type {
        border-bottom: none;
    }
`;

const TimeDay = styled.span`
    font-weight: 500;
    color: #374151;
    font-size: 12px;
`;

const TimeHours = styled.span`
    color: #6b7280;
    font-weight: 600;
    font-size: 12px;
`;

const ResponseTime = styled.div`
    margin-top: 16px;
    padding: 12px;
    background: rgba(37, 37, 37, 0.7);
    border-radius: 8px;
    color: #fff;
    font-size: 14px;
    text-align: center;
    font-weight: 500;
`;

const FaqSection = styled.section`
    margin-bottom: 40px;
`;

const FaqItem = styled.div`
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 16px;
    margin-bottom: 16px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: scale(1.02);
`;

const FaqQuestion = styled.button`
    width: 100%;
    padding: 20px 24px;
    background: ${({ $expanded }) => ($expanded ? "rgba(37, 37, 37, 0.05)" : "transparent")};
    border: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font-weight: 500;
    font-size: 16px;
    color: #374151;
    transition: all 0.2s ease;
    text-align: left;

    &:hover {
        background: rgba(37, 37, 37, 0.05);
    }
`;

const FaqAnswer = styled.div`
    max-height: ${({ $expanded }) => ($expanded ? "180px" : "0")};
    opacity: ${({ $expanded }) => ($expanded ? "1" : "0")};
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const FaqAnswerContent = styled.div`
    padding: 0 24px 20px;
    color: #6b7280;
    line-height: 1.6;
    font-size: 15px;
`;

const BottomMenuSpacer = styled.div`
    height: 80px;
`;
